import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Users,
  FileText,
  Activity,
  CheckCircle2,
  HardDrive,
  Plus,
  AlertTriangle,
  Lock,
  FolderOpen,
} from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import { usePermissions } from "@/hooks/usePermissions";
import { ftpApi } from "@/services/api/ftp";
import type { OrgStatus, OrgQuotaSummary } from "@/services/api/ftp";

export const Route = createFileRoute("/portal/ftp/")({
  component: FtpDashboard,
});

function FtpDashboard() {
  const { token } = useAuth();
  const { can } = usePermissions();
  // FT28: org_admin has create|read|update|delete but NOT `manage`, so the
  // "Add first user" CTA must gate on `create` (matches the users page).
  const canCreateUser = can("create", "ftp");

  const [status, setStatus] = useState<OrgStatus | null>(null);
  const [quotas, setQuotas] = useState<OrgQuotaSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!token) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function loadData() {
    setLoading(true);
    setError(false);
    try {
      // FT27: org status carries has_access + tier — it MUST load. Don't swallow
      // its failure (the old `.catch(() => null)` + `?? "starter"` painted a
      // healthy Starter dashboard for unsubscribed orgs and during CP outages,
      // then hit the first raw error on user-create). Quotas are best-effort —
      // a failure there just hides the usage bar, not the whole page. Kick quotas
      // off first (with its own .catch) so it runs in parallel with the required
      // status call and never unhandled-rejects if status throws.
      const quotaP = ftpApi.getOrgQuotas(token!).catch(() => null);
      const statusData = await ftpApi.getOrgStatus(token!);
      // An empty-body 2xx (portalFetch returns null) can't tell us has_access —
      // treat it as a load error, never a fabricated healthy dashboard.
      if (!statusData) throw new Error("empty SFTP status response");
      setStatus(statusData);
      setQuotas(await quotaP);
    } catch {
      setError(true);
      setStatus(null);
      setQuotas(null);
    } finally {
      setLoading(false);
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading SFTP status...</div>
      </div>
    );
  }

  // FT27: a load failure gets a visible, retryable error card — NOT a fabricated
  // healthy Starter dashboard that hides the outage until the first user-create.
  if (error) {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <AlertTriangle className="h-4 w-4 text-destructive" />
            Couldn&rsquo;t load SFTP status
          </CardTitle>
          <CardDescription>
            We couldn&rsquo;t reach the SFTP service. This is usually temporary.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="sm" onClick={() => loadData()}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  // FT27: an org without an SFTP subscription gets a dedicated state, not a
  // green-check "healthy Starter" dashboard it can't actually use.
  if (status && !status.has_access) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Lock className="h-4 w-4" />
            No active SFTP subscription
          </CardTitle>
          <CardDescription>
            Your organisation doesn&rsquo;t have an SFTP subscription yet. Contact
            your administrator or Larasoft to enable secure file transfer.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const tier = status?.tier ?? quotas?.tier ?? null;
  const tierLabel = tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : "Unknown";
  const storageGib = status?.tier_storage_gib ?? quotas?.tier_storage_gib ?? 0;
  const maxUsers = status?.tier_max_users ?? quotas?.tier_max_users ?? 0;
  const userCount = status?.user_count ?? quotas?.user_count ?? 0;
  const usedBytes = quotas?.total_used_bytes ?? 0;
  const totalBytes = quotas?.tier_storage_bytes ?? storageGib * 1024 * 1024 * 1024;
  const storagePct = totalBytes > 0 ? Math.min(100, (usedBytes / totalBytes) * 100) : 0;
  const hasUsers = userCount > 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-bold text-slate-900">SFTP Dashboard</h1>
        <p className="mt-2 text-lg text-slate-600">
          Secure file transfer for your organisation
          <span className="ml-2 inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
            {tierLabel}
          </span>
        </p>
      </div>

      {/* Storage and User stats */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Storage usage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <HardDrive className="h-4 w-4" />
              Storage
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold">{formatBytes(usedBytes)}</span>
              <span className="text-sm text-muted-foreground">
                of {storageGib} GB
              </span>
            </div>
            <Progress value={storagePct} className="h-2" />
            <p className="text-xs text-muted-foreground">
              {storagePct.toFixed(1)}% used
              {quotas ? ` \u00b7 ${formatBytes(quotas.remaining_bytes)} remaining` : ""}
            </p>
          </CardContent>
        </Card>

        {/* User count */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" />
              SFTP Users
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-baseline justify-between">
              <span className="text-2xl font-bold">{userCount}</span>
              <span className="text-sm text-muted-foreground">
                of {maxUsers === -1 ? "Unlimited" : maxUsers}
              </span>
            </div>
            {maxUsers > 0 && (
              <Progress
                value={Math.min(100, (userCount / maxUsers) * 100)}
                className="h-2"
              />
            )}
            {!hasUsers && canCreateUser && (
              <Link to="/portal/ftp/users">
                <Button size="sm" className="mt-2 gap-1">
                  <Plus className="h-3.5 w-3.5" />
                  Add your first SFTP user
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Connection details (always visible) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <CheckCircle2 className="h-4 w-4 text-green-500" />
            SFTP Connection Details
          </CardTitle>
          <CardDescription>
            Use these details in your SFTP client to connect
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-2 text-sm sm:grid-cols-2 md:grid-cols-3">
            <div>
              <span className="text-muted-foreground">Host:</span>{" "}
              <code className="rounded bg-muted px-1.5 py-0.5">sftp.larasoft.global</code>
            </div>
            <div>
              <span className="text-muted-foreground">Port:</span>{" "}
              <code className="rounded bg-muted px-1.5 py-0.5">2022</code>
            </div>
            <div>
              <span className="text-muted-foreground">Protocol:</span>{" "}
              <code className="rounded bg-muted px-1.5 py-0.5">SFTP</code>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quick links */}
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Link to="/portal/ftp/files">
          <Card className="cursor-pointer transition-colors hover:bg-muted/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FolderOpen className="h-4 w-4" />
                Files
              </CardTitle>
              <CardDescription>
                {can("create", "ftp") // same gate as the Files tab's Upload
                  ? "Browse, upload and download files"
                  : "Browse and download files"}
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Link to="/portal/ftp/users">
          <Card className="cursor-pointer transition-colors hover:bg-muted/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="h-4 w-4" />
                Users
              </CardTitle>
              <CardDescription>
                Manage SFTP users and passwords
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Link to="/portal/ftp/audit">
          <Card className="cursor-pointer transition-colors hover:bg-muted/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" />
                Audit Log
              </CardTitle>
              <CardDescription>
                View file transfer activity and events
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
        <Link to="/portal/ftp/connections">
          <Card className="cursor-pointer transition-colors hover:bg-muted/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Activity className="h-4 w-4" />
                Connections
              </CardTitle>
              <CardDescription>
                Monitor active SFTP sessions
              </CardDescription>
            </CardHeader>
          </Card>
        </Link>
      </div>
    </div>
  );
}
